<template>
        <ag-grid-vue style="width: 100%"
                     ref="gridRef"
                     :animateRows="true"
                     :accentedSort="true"
                     :aggFuncs="aggFuncs"
                     :autoGroupColumnDef="autoGroupColumnDef"
                     :cellFadeDelay="cellFadeDelay"
                     :cellFlashDelay="cellFlashDelay"
                     :cellSelection="cellSelection"
                     :columnDefs="columnDefs"
                     :columnTypes="columnTypes"
                     :defaultColDef="defaultColDef"
                     :enableCellChangeFlash="true"
                     :enableRangeSelection="enableRangeSelection"
                     :getDataPath="getDataPath"
                     :groupDefaultExpanded="groupDefaultExpanded"
                     :groupDisplayType="groupDisplayType"
                     :groupHeaderHeight="groupHeaderHeight"
                     :groupIncludeFooter="groupIncludeFooter"
                     :groupIncludeTotalFooter="groupIncludeTotalFooter"
                     :groupRowsSticky="true"
                     :headerHeight="headerHeight"
                     :initialGroupOrderComparator="ssrm ? undefined : initialGroupOrderComparator"
                     :localeText="localeText"
                     :pinnedBottomRowData="pinnedBottomRowData"
                     :refresh="refresh"
                     :rowData="ssrm ? undefined : rowData"
                     :rowModelType="ssrm ? 'serverSide' : undefined"
                     :serverSideDatasource="ssrm ? serverSideDatasource : undefined"
                     :cacheBlockSize="ssrm ? (ssrmBlockSize || 50) : undefined"
                     :pagination="ssrm ? true : undefined"
                     :paginationAutoPageSize="ssrm ? true : undefined"
                     :rowHeight="rowHeight"
                     :rowSelection="rowSelection"
                     :sendToClipboard="sendToClipboard"
                     :suppressFieldDotNotation="true"
                     :suppressClipboardPaste="true"
                     :treeData="treeData"
                     :theme="theme"
                     :undoRedoCellEditing="undoRedoCellEditing"
                     :undoRedoCellEditingLimit="undoRedoCellEditingLimit"
                     :domLayout="domLayout"
                     @cell-clicked="onCellClicked"
                     @cell-doubleClicked="onCellClicked"
                     @cell-editing-stopped="onCellEditingStopped"
                     @filter-changed="onFilterChanged"
                     @grid-ready="onGridReady"
                     @row-clicked="onRowClicked"
                     @row-data-updated="onRowDataUpdated"
                     @row-drag-end="onRowDragEnd"
                     @row-drag-move="onRowDragMove"
                     @row-drag-leave="onRowDragLeave"
                     @selection-changed="onSelectionChanged">
        </ag-grid-vue>
</template>

<script setup>

    // #region IMPORTS
    import { getCurrentInstance, inject, onBeforeMount, onMounted,onUpdated, ref, onUnmounted } from 'vue';
    import { useRoute } from 'vue-router';
    import { AgGridVue } from "ag-grid-vue3";
    import { CellApiModule,
            CellSelectionModule,
            CellStyleModule,
            CheckboxEditorModule,
            ClientSideRowModelApiModule,
            ClipboardModule,
            ColumnApiModule,
            ColumnAutoSizeModule,
            ColumnMenuModule,
            ContextMenuModule,
            CustomEditorModule,
            DateEditorModule,
            ExcelExportModule,
            HighlightChangesModule,
            LicenseManager,
            LocaleModule,
            ModuleRegistry,
            NumberEditorModule,
            PaginationModule,
            PinnedRowModule,
            QuickFilterModule,
            RenderApiModule,
            RichSelectModule,
            RowDragModule,
            RowGroupingModule,
            RowSelectionModule,
            SelectEditorModule,
            ServerSideRowModelModule,
            SetFilterModule,
            TextEditorModule,
            TreeDataModule,
            UndoRedoEditModule,
            ValidationModule } from 'ag-grid-enterprise';
    import { eCosTheme } from '@/assets/js/agGridCustomTheme';
    import { toast } from 'vue3-toastify';

    import appointmentCellRenderer from '@/components/agGridComponents/appointmentCellRenderer.vue';
    import attributeTypeCellRenderer from "@/components/agGridComponents/attributeTypeCellRenderer.vue";
    import databaseCellRenderer from '@/components/agGridComponents/databaseCellRenderer.vue';
    import deleteCellRenderer from '@/components/agGridComponents/deleteCellRenderer.vue';
    import editCellRenderer from '@/components/agGridComponents/editCellRenderer.vue';
    import tokenComponent from '@/components/agGridComponents/tokenComponent.vue';
    import favoriteCellRenderer from '@/components/agGridComponents/favoriteCellRenderer.vue';
    import fileSystemCellRenderer from '@/components/agGridComponents/fileSystemCellRenderer.vue';
    import manageCellRenderer from '@/components/agGridComponents/manageCellRenderer.vue';
    import sizeCellRenderer from '@/components/agGridComponents/sizeCellRenderer.vue';
    import userCellRenderer from '@/components/agGridComponents/userCellRenderer.vue';
    import colorPickerCellRenderer from '@/components/agGridComponents/colorPickerCellRenderer.vue';
    import attributeBooleanCellRenderer from '@/components/agGridComponents/attributeBooleanCellRenderer.vue';
    import activeUserInnerHeaderComponent from './agGridComponents/activeUserInnerHeaderComponent.vue';
    import localeInnerHeaderComponent from '@/components/agGridComponents/localeInnerHeaderComponent.vue';
    import translationValueCellRenderer from '@/components/agGridComponents/translationValueCellRenderer.vue';
    import playCellRenderer from "@/components/agGridComponents/playCellRenderer.vue";
    import restartCellRenderer from '@/components/agGridComponents/restartCellRenderer.vue';
    import logBatchStatusCellRenderer from '@/components/agGridComponents/logBatchStatusCellRenderer.vue';
    import logCellRenderer from '@/components/agGridComponents/logCellRenderer.vue';
    import { useControlStore } from '@/stores/controlStore';
    import { useLogStore } from '@/stores/logStore';
    import { useUserStore } from '@/stores/userStore';
    import {AG_GRID_LOCALE_FR, AG_GRID_LOCALE_EN} from '@ag-grid-community/locale';

    import ApplicationDomainJson from '@/components/json/editableApplicationDomain.json';

    ModuleRegistry.registerModules([
        CellApiModule,
        CellSelectionModule,
        CellStyleModule,
        CheckboxEditorModule,
        ClientSideRowModelApiModule,
        ClipboardModule,
        ColumnApiModule,
        ColumnAutoSizeModule,
        ColumnMenuModule,
        ContextMenuModule,
        CustomEditorModule,
        DateEditorModule,
        ExcelExportModule,
        HighlightChangesModule,
        LocaleModule,
        NumberEditorModule,
        PaginationModule,
        PinnedRowModule,
        QuickFilterModule,
        RenderApiModule,
        RichSelectModule,
        RowDragModule,
        RowGroupingModule,
        RowSelectionModule,
        SelectEditorModule,
        ServerSideRowModelModule,
        SetFilterModule,
        TextEditorModule,
        TreeDataModule,
        UndoRedoEditModule,
        ValidationModule
    ]);

    defineOptions({
        name: 'AgGrid',
        components: {
            AgGridVue,
            appointmentCellRenderer,
            attributeTypeCellRenderer,
            databaseCellRenderer,
            deleteCellRenderer,
            editCellRenderer,
            playCellRenderer,
            restartCellRenderer,
            tokenComponent,
            colorPickerCellRenderer,
            attributeBooleanCellRenderer,
            favoriteCellRenderer,
            fileSystemCellRenderer,
            localeInnerHeaderComponent,
            activeUserInnerHeaderComponent,
            translationValueCellRenderer,
            logCellRenderer,
            logBatchStatusCellRenderer,
            manageCellRenderer,
            sizeCellRenderer,
            userCellRenderer,
        },
    });

    // #endregion

    // #region PROPS & EMITS
    const { fetchUrl, 
        columnDefs, 
        autoGroupColumnDef, 
        groupDefaultExpanded, 
        enableRangeSelection, 
        treeData, 
        gridParams, 
        refresh, 
        refreshCallback, 
        filterChartData, 
        domLayout, 
        rowSelection, 
        ssrm, 
        ssrmBlockSize } = 
        defineProps(['fetchUrl', 
        'columnDefs', 
        'autoGroupColumnDef', 
        'groupDefaultExpanded', 
        'enableRangeSelection', 
        'treeData', 
        'gridParams', 
        'refresh', 
        'refreshCallback', 
        'filterChartData', 
        'domLayout', 
        'rowSelection', 
        'ssrm', 
        'ssrmBlockSize']);
    const emit = defineEmits(['clickEdit', 'manageOptions', 'clickDatabase', 'onGridReady', 'onRowDataUpdated', 'manageDistributors']);
    // #endregion

    const instance = getCurrentInstance();

    const urlWebAPI = inject('urlWebAPI');
    const getFormBody = inject('getFormBody');
    const stopLoading = inject('stopLoading');
    const gridRef = ref(null);
    const controlStore = useControlStore();
    const logStore = useLogStore();
    const userStore = useUserStore();
    const aggFuncs = ref(null);
    const gridApi = ref(null);
    const groupIncludeFooter = ref(instance.proxy.$parent.groupIncludeFooter);
    const groupIncludeTotalFooter = ref(instance.proxy.$parent.groupIncludeTotalFooter);
    const localeFR = ref(AG_GRID_LOCALE_FR);
    const localeEN = ref(AG_GRID_LOCALE_EN);
    const pinnedBottomRowData = ref(null);
    const rowData = ref(null);
    const refreshing = ref(false);
    const theme = ref(eCosTheme);
    const cellSelection = true;

    const cellFadeDelay = 2000;
    const cellFlashDelay = 3000;

    const getDataPath = (data) => {
        return data.path;
    };
    
    const localeText = ref(null);
    localeText.value = (userStore.getUser().locale != null && userStore.getUser().locale?.toLowerCase() == 'en') ? localeEN.value : localeFR.value;

    const defaultColDef = {
        context: {
            cellSelection: true,
        },
        cellClassRules: {
            'editable': (params) => {
                if (params.node.rowPinned != 'bottom') {
                    if (typeof params.colDef.editable != 'undefined' && typeof params.colDef.editable == 'function')
                        return params.colDef.editable(params);
                    else
                        return typeof params.node.data != 'undefined' && typeof params.colDef.editable != 'undefined' && params.colDef.editable;
                }
            },

            'module': (params) => {
                return typeof params.node.data != 'undefined' && typeof params.node.data.modules != 'undefined';
            },

            'child-row-group': (params) => {
                return !params.node.group && typeof params.node.parent != 'undefined' && params.node.parent.id != 'ROOT_NODE_ID' && params.api.getColumn(params.colDef.field) != null && params.api.getColumn(params.colDef.field).left == 0;
            },
        },
        // SSRM: server returns rows in a fixed order, client-side filter/sort would not apply to unloaded rows
        filter: ssrm ? false : 'agSetColumnFilter',
        menuTabs: ['filterMenuTab', 'columnsMenuTab', 'generalMenuTab'],
        flex: 1,
        resizable: true,
        sortable: !ssrm,
        getQuickFilterText: params => {
            if(params.value && typeof params.value === 'string') {
                return params.value.normalize("NFD").replace(/\p{Diacritic}/gu, "");
            }
        },
    };

    const groupDisplayType = getParentValue('groupDisplayType', 'groupRows');

    const initialGroupOrderComparator = (params) => {
        const a = params.nodeA.key || '';
        const b = params.nodeB.key || '';
        return a < b ? -1 : a > b ? 1 : 0;
    };

    const headerHeight = 50;
    const rowHeight = 40;
    const groupHeaderHeight = 50;
    const undoRedoCellEditing = true;
    const undoRedoCellEditingLimit = 20;

    const columnTypes = getColumnTypes();
    // #endregion

    // #region Lifecycle hooks
    onBeforeMount(() => {
        setLicenseKey();
        aggFuncs.value = {
            'applicationSum': params => {
                let sum = 0;
                sum += params.values.length;
                return sum;
            }
        };
    });

    onMounted(() => {
        gridApi.value = instance.refs.gridRef.api;
        logStore.log('Grid mounted for page '+useRoute().name+', api available', 'info');
    })

    onUnmounted(() => {
        controlStore.removeActiveGrids(gridApi.value);
    })

    // #region UPDATED
    onUpdated(() => {
        // ROW DATA REFRESH
        if (typeof __props.refresh != 'undefined' && (typeof __props.refresh != 'object' && __props.refresh || typeof __props.refresh == 'object' && __props.refresh.state && __props.refresh.type == 'data') && !refreshing.value) {
            refreshing.value = true;
            getGridData();
        }

        // OTHER REFRESH TYPE
        else if (typeof __props.refresh != 'undefined' && typeof __props.refresh == 'object' && __props.refresh.state && !refreshing.value)
            if (__props.refresh.type == 'column')
                gridApi.value.setGridOption('columnDefs', columnDefs);
    });

    // #endregion

    // #endregion
    
    // #region METHODS

    // A component-based dialog (v-dialog/v-card) sitting between AgGrid and the
    // page/tab component adds an extra level to the component tree, so fall back
    // to the grandparent when the immediate parent doesn't expose the value/handler.
    function getParentContext(name) {
        let ancestor = instance.proxy.$parent;
        let hops = 0;
        while (ancestor != null && hops < 10) {
            if (typeof ancestor[name] != 'undefined')
                return ancestor;

            ancestor = ancestor.$parent;
            hops++;
        }

        return null;
    }

    function getParentValue(name, defaultValue) {
        let ctx = getParentContext(name);
        return (ctx != null && ctx[name]) ? ctx[name] : defaultValue;
    }

    function setLicenseKey() {
        LicenseManager.setLicenseKey('Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-103370}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{EXTERNIS}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Ecos}_only_for_{1}_Front-End_JavaScript_developer___All_Front-End_JavaScript_developers_working_on_{Ecos}_need_to_be_licensed___{Ecos}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{15_July_2027}____[v3]_[0102]_MTgxNTYwNjAwMDAwMA==f2e9af1601fd804c4733ce38992ffe64');
    }

    // #region DATA

    async function getGridData() {
        if (typeof __props.fetchUrl !== 'undefined') {
            var formBody = getFormBody(gridParams);

            // DATA ROWS
            return fetch(urlWebAPI + __props.fetchUrl, {
                method: 'POST',
                body: formBody,
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                }
            })
            .then(result => result.json())
            .then(newRowData => {
                refreshing.value = false;
                newRowData = processData(newRowData);
                // check on isDestroyed added to be airtight on the rowData binding
                // in case someone like to switch between pages like a maniac
                if(!gridApi.value.isDestroyed()){
                    gridApi.value.setGridOption("rowData", newRowData);
                    setPinnedBottomRowData(gridApi.value.getGridOption("rowData"));
                }else{
                    rowData.value = newRowData;
                    setPinnedBottomRowData(rowData.value);
                }

                //updateCheckAll();

                // CALL BACK FUNCTION
                if (typeof refreshCallback != 'undefined' && typeof refreshCallback == 'function'){
                    refreshCallback();
                }

                stopLoading();
            })
            .catch(error => {
                logStore.log(error, 'error');
                toast.error('Error during executing ' + fetchUrl + ' data.');
                gridApi.value.setGridOption("loading", false);

                if (typeof controlStore.controls.customerSelect != 'undefined')
                    $(controlStore.controls.customerSelect.root).enable();
            });
        }
    }

    // SERVER-SIDE ROW MODEL - the grid asks for [startRow, endRow[ and the API answers { rows, lastRow }
    // lastRow = -1 means the total is unknown (more rows available)
    const serverSideDatasource = {
        getRows: async (params) => {
            const { startRow, endRow } = params.request;
            try {
                const response = await fetch(urlWebAPI + __props.fetchUrl, {
                    method: 'POST',
                    body: getFormBody({ ...gridParams, startRow, endRow }),
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
                    }
                });

                if (!response.ok)
                    throw new Error(await response.text());

                const result = await response.json();
                params.success({
                    rowData: result.rows,
                    rowCount: result.lastRow >= 0 ? result.lastRow : undefined,
                });
            }
            catch (error) {
                logStore.log(error, 'error');
                toast.error('Error during executing ' + fetchUrl + ' data.');
                params.fail();
            }
        }
    };

    function updateExistingUpdatedRow(params, key) {
        let parentRowsUpdated = getParentContext('rowsUpdated').rowsUpdated;
        let existingData = parentRowsUpdated.filter(ru => ru[key] == params.data[key]);

        if (existingData.length == 0)
            parentRowsUpdated.push(params.data);
        else
            existingData[0] = params.data;
    }

    function processData(rowData) {
        switch (fetchUrl) {
            case 'GetAttributes':
                instance.proxy.$parent.attributes = rowData;
                break;

            case 'GetDistributors':
                emit('manageDistributors', rowData);
                break;

            case 'GetUsers':
                instance.proxy.$parent.usersAdmin = rowData;
                break;

            case 'GetAgCronTab':
                //instance.proxy.$parent.getChartData();
                break;

            case 'GetOptions':
                emit('manageOptions', rowData);
                break;
        }

        return rowData;
    }

    function setPinnedBottomRowData(rows) {
        let result = [];
        let pinnedLine = {};
        if(!gridApi.value.isDestroyed()){
            for (let row of rows) {
                for (let key of Object.keys(row)) {
                    // CREATE KEY IF NOT EXIST
                    if (pinnedLine[key] == null)
                        pinnedLine[key] = 0;

                    // SUM
                    if (typeof row[key] != 'undefined' && row[key] != null && row[key] != '' && row[key] != 0)
                        // STRING COUNT
                        if (typeof row[key] == 'string' || typeof row[key] == 'boolean')
                            pinnedLine[key]++;

                        // NUMBER SUM
                        else if (typeof row[key] == 'number')
                            pinnedLine[key] += row[key];

                }
            }

            result.push(pinnedLine);
            pinnedBottomRowData.value = result;
            gridApi.value.setGridOption('pinnedBottomRowData', result);
        }
    }

    // #endregion

    // #region UI
    function updateCheckAll() {
        if ($('.checkbox-all').length > 0) {
            var columnField = $('.checkbox-all > div').attr('data-column');
            var nbEnabled = 0;
            var nbDisabled = 0;

            if (!gridApi.value.destroyCalled) {
                gridApi.value.forEachLeafNode((node, index) => {
                    if (node.data[columnField])
                        nbEnabled++;
                    else
                        nbDisabled++;
                });

                // INDETERMINATE
                if (nbEnabled > 0 && nbDisabled > 0) {
                    $('.checkbox-all input').prop('checked', false);
                    $('.checkbox-all input').attr('data-indeterminate', true);
                }

                // ALL ACTIVE
                else if (nbEnabled > 0) {
                    $('.checkbox-all input').removeAttr('data-indeterminate');
                    $('.checkbox-all input').prop('checked', true);
                }
            }
        }
    }

    // #endregion

    // #region GET

    function getColumnTypes() {
        return {
            // #region GLOBAL TYPES

            boolean: {
                cellEditorSelector: (params) => {
                    return {
                        component: 'agCheckboxCellEditor',
                    }
                },
                cellRenderer: 'agCheckboxCellRenderer',
            },

            booleanEditable: {
                cellEditorSelector: (params) => {
                    return {
                        component: 'agCheckboxCellEditor',
                    }
                },
                cellRenderer: 'agCheckboxCellRenderer',
                filterParams: {
                    cellRenderer: 'agCheckboxCellRenderer',
                },
                editable: (params) => {
                    if (params.node.rowPinned != 'bottom')
                        return params.column.colDef.field != 'agBatchEnabled' || params.column.colDef.field == 'agBatchEnabled' && params.data.agBatchName != null;

                    return false;
                },

                valueGetter: (params) => {
                    if (typeof params.data != 'undefined' && typeof params.data[params.colDef.field] != 'undefined' && params.data[params.colDef.field] != null && (params.column.colDef.field != 'agBatchEnabled' || params.column.colDef.field == 'agBatchEnabled' && params.data.agBatchName != null))
                        return params.data[params.colDef.field];
                    else
                        return null;
                }
            },

            date: {
                cellEditor: 'agDateStringCellEditor',
                valueFormatter: (params) => {
                    var formattedValue = params.value;
                    //if we're not on the pinned bottom row then we format
                    if(typeof params.value == 'string'){
                        if (params.column.colDef.field == 'createdDate' || params.column.colDef.field == 'modifiedDate' || params.column.colDef.field == 'lastLogonDate' || params.column.colDef.field =='logDate')
                            formattedValue = new Date(params.value).getFullYear() > 1 ?
                                                new Date(new Date(params.value).setUTCHours(-1)).toLocaleDateString() + ' ' + params.value.split('T')[1].split('.')[0] :
                                                '';
                        // DATE
                        else
                            formattedValue = new Date(params.value).getFullYear() > 1 ?
                                new Date(new Date(params.value).setUTCHours(-1)).toLocaleDateString() :
                                '';
                    }
                    return formattedValue
                },
                valueGetter: (params) => {
                    var returnValue = null;
                    //We get the params.data[coldef.field]  only if params.data do have a property name after the column
                    //This specific case for the ‘if’ condition comes up in serverFilesDialog, where the file rows do not include a ‘modification date’ property, contrary to what the grid expects
                    if(params.data !== undefined){
                        if(Object.keys(params.data).includes(params.colDef.field)){
                            returnValue = params.data[params.colDef.field]
                        }
                        else{
                            returnValue ='';
                        }
                    }

                    return returnValue
                },
                valueSetter: (params) => {
                    params.data[params.colDef.field] = params.newValue == '' ? '0001-01-01T00:00:00Z' : new Date(params.newValue).toISOString();
                    return true
                },

                minWidth: 200,
                maxWidth: 200,
            },

            // #endregion

            // #region CUSTOM TYPES

            delayHour: {
                cellEditor: 'agNumberCellEditor',
                valueFormatter: (params) => {
                    return params.value !== 0 ? params.value + ' H' : '';
                },
                minWidth: 100,
            },

            fileSystem: {
                cellRenderer: 'fileSystemCellRenderer',
                filterParams: {
                    cellRenderer: 'fileSystemCellRenderer',
                },
            },

            language: {
                valueGetter: (params) => {
                    if (typeof params.data != 'undefined' && typeof params.data.locales[params.colDef.field.toLowerCase()] != 'undefined')
                        return params.data.locales[params.colDef.field.toLowerCase()];
                    else
                        return null;
                },
                valueSetter: (params) => {
                    params.data.locales[params.colDef.field.toLowerCase()] = params.newValue;
                    return true;
                }
            },

            logBatchStatus: {
                cellRenderer: 'logBatchStatusCellRenderer',
                filterParams: {
                    cellRenderer: 'logBatchStatusCellRenderer',
                },
            },

            module: {
                // STANDARD AG GRID BOOLEAN CELL - CHECKBOX RENDERER + EDITOR, DISABLED AUTOMATICALLY WHEN NOT EDITABLE
                cellDataType: 'boolean',
                cellRenderer: 'agCheckboxCellRenderer',
                cellEditor: 'agCheckboxCellEditor',

                valueGetter: (params) => {
                    if (typeof params.data == 'undefined')
                        return undefined;

                    let editableApplicationDomains = ApplicationDomainJson;
                    let editableApplicationDomain = editableApplicationDomains[params.colDef.field];

                    let isVisible = typeof editableApplicationDomain != 'undefined' && (editableApplicationDomain.visible.indexOf(params.data.process) > -1 || typeof params.data.customerCode == 'string' && editableApplicationDomain.visible.indexOf(params.data.customerCode.toLowerCase()) > -1);

                    // MODULE NOT APPLICABLE FOR THIS DOMAIN - LEAVE THE CHECKBOX BLANK/INDETERMINATE
                    if (!isVisible)
                        return undefined;

                    return typeof params.data.modules[params.colDef.field] != 'undefined';
                },
                valueSetter: (params) => {
                    if (!params.newValue)
                        delete params.data.modules[params.colDef.field];
                    else
                        params.data.modules[params.colDef.field] = params.newValue;

                    return true;
                },
                editable: (params) => {
                    if (params.node.rowPinned != 'bottom') {
                        let editableApplicationDomains = ApplicationDomainJson;
                        let editableApplicationDomain = editableApplicationDomains[params.colDef.field];

                        if (params.colDef.field.toLowerCase() == 'stock' && typeof params.data.customerCode == 'string' && params.data.customerCode.toLowerCase() == 'kit')
                            return false;

                        if (typeof editableApplicationDomain != 'undefined' && editableApplicationDomain.editable.indexOf(params.data.process) != -1)
                            return true;

                        else if (typeof editableApplicationDomain == 'undefined')
                            logStore.log(`Module ${params.colDef.field} was not found. Check module table`, 'warning');
                    }

                    return false;
                },
            },

            size: {
                aggFunc: 'sum',
                cellRenderer: 'sizeCellRenderer',
                filterParams: {
                    cellRenderer: 'sizeCellRenderer',
                },
                minWidth: 120,
                maxWidth: 120,
            },

            // #endregion

            // #region ACTION TYPES
            appointmentAction: {
                cellRenderer: 'appointmentCellRenderer',
                width: 100,
                maxWidth: 100,
                resizable: false,
                filter: false,
                lockVisible: true,
                menuTabs: [],
                sortable: false,
                suppressHeaderMenuButton: true,
            },

            databaseAction: {
                cellRenderer: 'databaseCellRenderer',
                width: 100,
                maxWidth: 100,
                resizable: true,
                filter: false,
                lockVisible: true,
                menuTabs: [],
                sortable: false,
                suppressHeaderMenuButton: true,
            },

            deleteAction: {
                cellRenderer: 'deleteCellRenderer',
                width: 100,
                minWidth: 100,
                resizable: true,
                filter: false,
                lockVisible: true,
                menuTabs: [],
                sortable: false,
                suppressHeaderMenuButton: true,
            },

            editAction: {
                cellRenderer: 'editCellRenderer',
                width: 100,
                maxWidth: 100,
                resizable: false,
                filter: false,
                lockVisible: true,
                menuTabs: [],
                sortable: false,
                suppressHeaderMenuButton: true,
            },

            favoriteAction: {
                cellRenderer: 'favoriteCellRenderer',
                width: 100,
                maxWidth: 100,
                resizable: false,
                filter: true,
                lockVisible: true,
                sortable: false,
                suppressHeaderMenuButton: true,
            },

            logAction: {
                cellRenderer: 'logCellRenderer',
                width: 100,
                maxWidth: 100,
                resizable: false,
                filter: false,
                lockVisible: true,
                menuTabs: [],
                sortable: false,
                suppressHeaderMenuButton: true,
            },

            manageAction: {
                cellRenderer: 'manageCellRenderer',
                width: 100,
                maxWidth: 100,
                resizable: false,
                filter: false,
                lockVisible: true,
                menuTabs: [],
                sortable: false,
                suppressHeaderMenuButton: true,
            },

            playAction: {
                cellRenderer: 'playCellRenderer',
                width: 80,
                maxWidth: 80,
                resizable: true,
                flex:1,
                filter: false,
                lockVisible: true,
                menuTabs: [],
                sortable: false,
                suppressHeaderMenuButton: true,
            },

            restartAction: {
                cellRenderer: 'restartCellRenderer',
                width: 100,
                maxWidth: 100,
                resizable: false,
                filter: false,
                lockVisible: true,
                menuTabs: [],
                sortable: false,
                suppressHeaderMenuButton: true,
            },

            userAction: {
                cellRenderer: 'userCellRenderer',
                width: 100,
                maxWidth: 100,
                resizable: false,
                filter: false,
                lockVisible: true,
                menuTabs: [],
                sortable: false,
                suppressHeaderMenuButton: true,
            },

            // #endregion
        };
    }

    // #endregion

    // #region EVENTS

    function onGridReady(params) {
        controlStore.grids.push(params.api);
        gridApi.value = params.api;
        emit('onGridReady', params);
    }

    function onRowDataUpdated(params) {
        // CHECK ALL UPDATE
        if (controlStore.checkAllUpdate) {
            let parentCtx = getParentContext('rowsUpdated');
            parentCtx.rowsUpdated = [];

            params.api.forEachLeafNode(function (node) {
                parentCtx.rowsUpdated.push(node.data);
            });
        }
        emit('onRowDataUpdated', params);
    }

    function onFilterChanged(params) {
        // VIEW SCHEDULER - REFRESH CHART DATA
        if (typeof filterChartData != 'undefined')
            filterChartData(params);
    }

    function onSelectionChanged(params) {
        let ctx = getParentContext('onSelectionChanged');

        if (ctx != null && (typeof params.node != 'undefined' && params.node.rowPinned != 'bottom' || typeof params.node == 'undefined'))
            return ctx.onSelectionChanged(params);
    }

    function onRowClicked(params) {
        let ctx = getParentContext('onRowClicked');

        if (ctx != null && (typeof params.node != 'undefined' && params.node.rowPinned != 'bottom' || typeof params.node == 'undefined'))
            return ctx.onRowClicked(params);
    }

    function onCellClicked(params) {
        let ctx = getParentContext('onCellClicked');
        if (ctx != null && (typeof params.node != 'undefined' && params.node.rowPinned != 'bottom' || typeof params.node == 'undefined'))
            return ctx.onCellClicked(params);

        switch (params.column.colId) {
            case 'edit':
                emit('clickEdit', params.data);
                break;
            case 'database':
                emit('clickDatabase', params.data);
                break;
        }
    }

    function onCellDoubleClicked(params) {
        let ctx = getParentContext('onCellDoubleClicked');

        if (ctx != null && (typeof params.node != 'undefined' && params.node.rowPinned != 'bottom' || typeof params.node == 'undefined'))
            return ctx.onCellDoubleClicked(params);
    }

    function onCellEditingStopped(params) {
        var activeTab = $('.tab-content--active').length > 0 ? $('.tab-content--active').attr('id') : null;

        // PARSE BOOLEAN STRING
        if (typeof params.oldValue == 'string' && params.oldValue != null && (params.oldValue.toLowerCase() == 'true' || params.oldValue.toLowerCase() == 'false'))
            params.oldValue = params.oldValue.toLowerCase() == 'true';

        var valueChanged = params.newValue != params.oldValue;
        let rowsUpdatedCtx = getParentContext('rowsUpdated');

        if (valueChanged && rowsUpdatedCtx != null) {
            let key = Object.keys(params.data).indexOf('customerCode') > -1 ? 'customerCode' : 'id';
            updateExistingUpdatedRow(params, key);

            // #region OPTIONS

            if (fetchUrl == 'GetOptions') {
                var optionName = params.data['name'];

                // SINGLE KEY UPDATE
                if (optionName == 'SingleKeyCustomerCode' || optionName == 'SingleKeyEAN') {
                    var optionValue = params.data['value'].toString() == 'true';
                    var targetOption = null;

                    if (optionName == 'SingleKeyCustomerCode')
                        targetOption = 'SingleKeyEAN';
                    else
                        targetOption = 'SingleKeyCustomerCode';

                    params.api.forEachNode((node, index) => {
                        if (typeof node.data != 'undefined' && node.data['name'] == targetOption) {
                            node.data['value'] = !optionValue;
                            updateExistingUpdatedRow(node, 'id');
                        }
                    });

                    params.api.redrawRows();
                }

                // CURRENCY UPDATE
                if (optionName == 'Currency') {
                    params.data['value'] = params.data['value'].substring(0, 3);
                }
            }

            // #endregion

            // #region TRANSLATION

            // LOCALE UPDATE TO CUSTOM TYPE
            if (typeof params.data.locales != 'undefined') {
                var lastRowUpdated = rowsUpdatedCtx.rowsUpdated.last();
                var isCustom = (typeof lastRowUpdated.localesCustom[params.colDef.field.toLowerCase()] != 'undefined') ? lastRowUpdated.localesCustom[params.colDef.field.toLowerCase()] : true;

                // CONVERT NOT CUSTOM TO CUSTOM - RESET VALUE ID
                if (!isCustom)
                    lastRowUpdated.localeIds[params.colDef.field.toLowerCase()] = 0;

                lastRowUpdated.localesCustom[params.colDef.field.toLowerCase()] = true;

                // REFLECT THE "IS CUSTOM" ICON IMMEDIATELY ON THE SIBLING COLUMN
                params.api.refreshCells({
                    rowNodes: [params.node],
                    columns: [`isCustom${params.colDef.field}`],
                    force: true,
                });
            }

            // #endregion

            // #region UI

            if (rowsUpdatedCtx.rowsUpdated.length > 0) {
                if (typeof controlStore.controls.saveCustomerButton != 'undefined') {
                    $(controlStore.controls.saveCustomerButton.root).enable();

                    // DISABLE TABS
                    controlStore.controls.customerTabBar.tabList.map(t => t.root).forEach((tab, index) => {
                        if ($(tab).attr('id').replace('Button', '') != activeTab)
                            $(tab).disable();
                    });
                }

                if (typeof controlStore.controls.undoAllButton != 'undefined')
                    $(controlStore.controls.undoAllButton.root).enable();

                if (typeof controlStore.controls.saveUsersButton != 'undefined')
                    $(controlStore.controls.saveUsersButton.root).enable();
            }

            // #endregion

            updateCheckAll();

            // KEEP OPEN CURRENT ROW GROUP
            setTimeout(() => {
                if (typeof params.node.parent != 'undefined' && typeof params.node.parent.group != 'undefined' && params.node.parent.group) {
                    var parentRowNode = params.api.getDisplayedRowAtIndex(params.node.parent.rowIndex);
                    params.api.setRowNodeExpanded(parentRowNode, true);
                }
            }, 10);
        }
    }

    function onRowDragEnd(params) {
        let ctx = getParentContext('onRowDragEnd');

        if (ctx != null)
            return ctx.onRowDragEnd(params);
    }

    function onRowDragMove(params) {
        if (typeof instance.proxy.$parent.onRowDragMove != 'undefined')
            return instance.proxy.$parent.onRowDragMove(params);
    }

    function onRowDragLeave(params) {
        if (typeof instance.proxy.$parent.onRowDragLeave != 'undefined')
            return instance.proxy.$parent.onRowDragLeave(params);
    }

    function sendToClipboard(params) {
        var value = params.data;

        // ENCORE TOKEN TO CLIPBOARD
        if (value.length > 100)
            value = encodeURIComponent(value);

        window.navigator.clipboard.writeText(value);
    }

    // #endregion

    // #region UPDATE

    function emptyRows() {
        if(!gridApi.value.isDestroyed()){
            gridApi.value.setGridOption('rowData', []);
            gridApi.value.setGridOption("loading", true);
        }
    }

    // #endregion

    // #endregion

    defineExpose({
        rowData,
        gridRef,
        getGridData,
        setPinnedBottomRowData
    })
</script>

<style lang="scss" scoped>
    @use '../assets/css/scss/_agGrid.scss';
</style>
